import { FaTimes } from "react-icons/fa";
import { DELETED_STAFF } from "../../constants";
import type OrderItem from "../../models/OrderItem";

type OrderDetailsProps = {
  isOpen: boolean;
  onClose: () => void;
  guestName: string;
  purchaseDate: string;
  paymentMethod: string;
  paymentStatus: string;
  isPaidByGuest?: boolean;
  orderedBy: { name: string; portfolio: string };
  clearedBy: { name: string; portfolio: string } | null;
  amount: number;
  discount?: number;
  orderNumber: string;
  description?: string;
  items: OrderItem[];
};

export default function OrderDetailsModal({
  isOpen,
  onClose,
  guestName,
  purchaseDate,
  paymentMethod,
  paymentStatus,
  orderedBy,
  clearedBy,
  isPaidByGuest,

  // amount,
  discount = 0,
  orderNumber,
  description,
  items,
}: OrderDetailsProps) {
  if (!isOpen) return null;

  const amount = items.reduce((sum, item) => sum + item.amount, 0);
  const isPaid = paymentStatus.toLocaleLowerCase() === "paid";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-20 p-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-gray-200 p-6 relative print:max-w-none print:w-[700px] print:mx-auto print:p-4 print:rounded-none">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl font-bold print:hidden"
        >
          {FaTimes({})}
        </button>

        {/* Header */}
        <div className="text-center mb-4">
          <h2 className="text-2xl font-bold text-gray-900">Order Details</h2>
          <p className="text-sm text-gray-500">{purchaseDate}</p>
        </div>

        {/* Order Info */}
        <div className="grid grid-cols-2 gap-2 text-sm text-gray-700 mb-4">
          <p>
            <span className="font-semibold">Order No.: #</span>{orderNumber}
          </p>
          <p>
            <span className="font-semibold">Guest:</span> {guestName}
          </p>
          <p>
            <span className="font-semibold">Payment Method:</span> {paymentMethod}
          </p>
          <p>
            <span className="font-semibold">Payment Status:</span>{" "}
            {paymentStatus.toLocaleLowerCase() === "paid" ? (
              <span className="text-green-600 font-medium">Paid</span>
            ) : (
              <span className="text-red-600 font-medium">Pending</span>
            )}
          </p>
          <p>
            <span className="font-semibold">Ordered By:</span>{" "}
            {orderedBy.name || DELETED_STAFF} {" "}
            {orderedBy.name ? `(${orderedBy?.portfolio})` : ""}
          </p>
          {paymentStatus.toLocaleLowerCase() === "paid" && (
            <p>
              <span className="font-semibold">Cleared By:</span>{" "}
              {!!isPaidByGuest && isPaid ? "Paid by Guest" : clearedBy?.name || DELETED_STAFF}
              {clearedBy?.name ? `(${clearedBy?.portfolio})` : ""}
            </p>
          )}
          {description && (
            <p className="col-span-2">
              <span className="font-semibold">Description:</span> {description}
            </p>
          )}
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200 my-3"></div>

        {/* Order Items Table */}
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Order Items</h3>
        <div className="max-h-48 overflow-y-auto border rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 sticky top-0">
              <tr>
                <th className="p-2 border">Item</th>
                <th className="p-2 border">Qty</th>
                <th className="p-2 border">Unit Price</th>
                <th className="p-2 border">Total</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={idx} className="even:bg-gray-50">
                  <td className="p-2 border text-center">{item.itemName}</td>
                  <td className="p-2 border text-center">{item.quantity}</td>
                  <td className="p-2 border text-center">{(item.amount / item.quantity).toLocaleString()}</td>
                  <td className="p-2 border text-center">{(item.amount).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Amount Summary */}
        <div className="mt-4 space-y-1 text-right text-gray-800">
          {/* {discount > 0 && (
            <div>
              <span className="font-medium">Subtotal:</span> ₦{amountBeforeDiscount.toLocaleString()}
            </div>
          )}
          {discount > 0 && (
            <div>
              <span className="font-medium text-red-600">Discount:</span> <span className="text-red-600">- ₦{discount.toLocaleString()}</span>
            </div>
          )} */}
          <div className="text-xl font-bold">
            Total: ₦{(amount - discount).toLocaleString()}
          </div>
        </div>
      </div>
    </div>
  );
}
